/**
 * AURELIA — component-cart.js
 * <cart-drawer>           The slide-out cart.
 * <cart-page>             The full cart template.
 * <cart-recommendations>  "Pairs well with" list inside the drawer.
 * <cart-discount>         Discount code entry.
 *
 * Rendering rule: this file never builds cart markup. Every visual update
 * comes from HTML that Liquid rendered, delivered by the Section Rendering
 * API alongside the cart mutation. That is what keeps discounts, market
 * pricing and gift cards correct — none of which can be computed client-side.
 */
(function () {
  'use strict';

  const { Element, Drawer, cart, utils, a11y, config, emit } = window.Aurelia;

  /* =====================================================================
     Cart drawer
     ===================================================================== */
  class CartDrawer extends Drawer {
    setup() {
      this.sectionId = this.dataset.sectionId;

      // Register with the cart so every mutation returns this section's HTML
      // in the same response — one request, no follow-up fetch.
      cart.registerSections([this.sectionId]);

      this.subscribe('cart:updated', (detail) => this.render(detail));
      this.bindContents();
    }

    /** (Re)binds controls that are replaced whenever the drawer re-renders. */
    bindContents() {
      this.$$('[data-cart-remove]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.removeLine(link);
        });
      });

      const note = this.$('[data-cart-note]');
      if (note && !note.dataset.bound) {
        note.dataset.bound = 'true';
        const save = utils.debounce((value) => cart.update({ note: value }), 700);
        note.addEventListener('input', (event) => save(event.target.value));
      }
    }

    async removeLine(link) {
      const item = link.closest('[data-cart-item]');
      if (item) item.setAttribute('aria-busy', 'true');

      try {
        await cart.remove(link.dataset.lineKey);
      } catch (error) {
        // The line could not be removed — let the customer use the real link.
        window.location.href = link.href;
      }
    }

    render({ sections }) {
      const html = sections && sections[this.sectionId];
      if (!html) return;

      // Preserve the open state and scroll position across the swap so the
      // list does not jump when a quantity changes.
      const body = this.$('.drawer__body');
      const scrollTop = body ? body.scrollTop : 0;

      const parsed = utils.parseHTML(html);
      const fresh = parsed.querySelector('cart-drawer');
      if (!fresh) return;

      this.innerHTML = fresh.innerHTML;

      const newBody = this.$('.drawer__body');
      if (newBody) newBody.scrollTop = scrollTop;

      this.bindContents();
      this.loadRecommendations();

      // The close buttons and overlay were just replaced, but Drawer matches
      // them from the click target rather than holding references, so the
      // swap needs no re-binding here.
      emit('content:appended', { target: this });
    }

    onOpen() {
      this.loadRecommendations();
    }

    loadRecommendations() {
      const node = this.querySelector('cart-recommendations');
      if (node && typeof node.load === 'function') node.load();
    }
  }

  window.Aurelia.define('cart-drawer', CartDrawer);

  /* =====================================================================
     Cart page
     ===================================================================== */
  class CartPage extends Element {
    mount() {
      this.sectionId = this.dataset.sectionId;
      cart.registerSections([this.sectionId]);

      this.subscribe('cart:updated', (detail) => this.render(detail));
      this.bindContents();
    }

    bindContents() {
      this.$$('[data-cart-remove]').forEach((link) => {
        link.addEventListener('click', async (event) => {
          event.preventDefault();
          const row = link.closest('[data-cart-item]');
          if (row) row.setAttribute('aria-busy', 'true');
          try {
            await cart.remove(link.dataset.lineKey);
          } catch (error) {
            window.location.href = link.href;
          }
        });
      });

      const note = this.$('[data-cart-note]');
      if (note && !note.dataset.bound) {
        note.dataset.bound = 'true';
        const save = utils.debounce((value) => cart.update({ note: value }), 700);
        note.addEventListener('input', (event) => save(event.target.value));
      }
    }

    render({ sections }) {
      const html = sections && sections[this.sectionId];
      if (!html) {
        window.location.reload();
        return;
      }

      const fresh = utils.parseHTML(html).querySelector('cart-page');
      if (!fresh) {
        window.location.reload();
        return;
      }

      this.innerHTML = fresh.innerHTML;
      this.bindContents();
      emit('content:appended', { target: this });
    }
  }

  window.Aurelia.define('cart-page', CartPage);

  /* =====================================================================
     Recommendations
     ===================================================================== */
  class CartRecommendations extends Element {
    mount() {
      this.list = this.$('[data-recommendations-list]');
      this.loaded = false;
    }

    async load() {
      if (this.loaded || !this.list) return;

      const productId = this.dataset.productId;
      if (!productId) return;

      this.loaded = true;

      const url = new URL(config.routes.product_recommendations_url, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', this.dataset.limit || 4);
      url.searchParams.set('intent', this.dataset.intent || 'related');
      url.searchParams.set('section_id', 'cart-recommendation-card');

      try {
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) return;

        const html = await response.text();
        const cards = utils.parseHTML(html).querySelectorAll('[data-recommendation-card]');
        if (cards.length === 0) return;

        const fragment = document.createDocumentFragment();
        cards.forEach((card) => {
          const listItem = document.createElement('li');
          listItem.appendChild(card);
          fragment.appendChild(listItem);
        });

        this.list.innerHTML = '';
        this.list.appendChild(fragment);
        this.hidden = false;

        emit('content:appended', { target: this });
      } catch (error) {
        // Recommendations are a nicety; a failure must never surface to the
        // customer or block the cart.
        this.loaded = false;
      }
    }
  }

  window.Aurelia.define('cart-recommendations', CartRecommendations);

  /* =====================================================================
     Discount code
     ===================================================================== */
  class CartDiscount extends Element {
    mount() {
      this.form = this.$('form');
      this.input = this.$('input[name="discount"]');
      if (!this.form || !this.input) return;

      this.listen(this.form, 'submit', (event) => this.apply(event));
    }

    /**
     * Shopify has no public "apply discount" cart endpoint. The supported
     * mechanism is the /discount/CODE URL, which sets the code in the session
     * and redirects. Fetching it applies the code without leaving the page.
     */
    async apply(event) {
      event.preventDefault();

      const code = this.input.value.trim();
      if (!code) return;

      const button = this.form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');

      try {
        await fetch(`${config.routes.root}discount/${encodeURIComponent(code)}`, {
          method: 'GET',
          redirect: 'follow',
        });
        // Re-read the cart so any cart-level discount is reflected in totals.
        await cart.fetch();
        this.input.value = '';
        a11y.announce(this.dataset.appliedMessage || '');
      } catch (error) {
        a11y.announce(this.dataset.errorMessage || config.strings.cartError, true);
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    }
  }

  window.Aurelia.define('cart-discount', CartDiscount);
})();
